import React, { useState } from 'react';
import { ClientEntity } from '../types';
import { useStore } from '../store/useStore';
import { 
  Bot, MessageSquare, ShieldCheck, Zap, Database, ArrowRight, 
  Send, Users, Calendar, Sparkles, Check, CheckCircle2, 
  Plus, Trash2, Settings, QrCode, Phone, Smartphone, AlertCircle, 
  RefreshCw, PlayCircle, Filter as Funnel, Save, Copy, ExternalLink, X
} from 'lucide-react';
import { toast } from 'sonner';

export const WhatsAppAgentDashboard: React.FC<{ client: ClientEntity }> = ({ client }) => {
  const { appointments } = useStore();
  const [activeTab, setActiveTab] = useState<'geral' | 'cerebro' | 'filtro' | 'handoff' | 'agenda'>('geral');
  
  // Connection state
  const [isConnected, setIsConnected] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('11 98765-4321');
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);

  // Brain & Simulator state
  const [personality, setPersonality] = useState<'consultivo' | 'direto'>('consultivo');
  const [prompt, setPrompt] = useState(
    `Você é a Lia, assistente virtual inteligente da empresa ${client.data.name}. Seu objetivo é tirar dúvidas sobre nossos serviços (${client.data.categories?.join(', ') || 'serviços gerais'}), entender a urgência do lead e qualificar se tem interesse antes de transferir para nosso consultor humano.`
  );
  const [sources, setSources] = useState([
    { id: 1, title: 'Tabela de Serviços & Valores.pdf', size: '1.2 MB', updated: 'Ontem' },
    { id: 2, title: 'Perguntas Frequentes & Garantias.docx', size: '450 KB', updated: '3 dias atrás' }
  ]);
  const [isAddSourceModalOpen, setIsAddSourceModalOpen] = useState(false);
  const [newSourceTitle, setNewSourceTitle] = useState('');

  // Simulator chat state
  const [chat, setChat] = useState([
    { role: 'ai', text: `Olá! Sou a assistente virtual da ${client.data.name}. Como posso te ajudar hoje?` }
  ]);
  const [msg, setMsg] = useState('');
  const [isAiTyping, setIsAiTyping] = useState(false);

  // Qualification Rules state
  const [rules, setRules] = useState([
    { id: 1, name: 'Entendimento da Necessidade', type: 'qualitativo', question: 'Qual o principal serviço ou solução que você está buscando hoje?' },
    { id: 2, name: 'Expectativa de Prazo & Orçamento', type: 'quantitativo', question: 'Qual a sua expectativa de prazo ou investimento para esse projeto?' },
  ]);
  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false);
  const [newRuleName, setNewRuleName] = useState('');
  const [newRuleQuestion, setNewRuleQuestion] = useState('');
  const [newRuleType, setNewRuleType] = useState('qualitativo');

  // Handoff state
  const [handoffTriggers, setHandoffTriggers] = useState([
    { id: 1, label: 'Pedido Explícito do Lead', description: 'Ex: "Quero falar com humano" ou "Pode me ligar?"', active: true },
    { id: 2, label: 'Filtro de Qualificação Completo', description: 'Após responder todas as perguntas de qualificação com sucesso', active: true },
    { id: 3, label: 'Dificuldade de Resposta', description: 'Quando a IA não encontra a resposta na base 3 vezes consecutivas', active: true },
  ]);
  const [notifyWhatsapp, setNotifyWhatsapp] = useState(true);
  const [notifyCrm, setNotifyCrm] = useState(true);

  // Reminders state
  const [reminders, setReminders] = useState([
    { id: 1, title: 'No momento do Agendamento', color: 'blue', text: 'Olá! Seu agendamento foi confirmado para [DATA] às [HORA]. Te enviamos os detalhes por aqui.', active: true },
    { id: 2, title: '24 Horas Antes', color: 'orange', text: '“Olá! Passando para lembrar da nossa reunião amanhã às [HORA]. Tudo certo para confirmarmos?”', active: true },
    { id: 3, title: '2 Horas Antes', color: 'emerald', text: 'Aviso final com link de acesso ou endereço: estamos prontos para te receber em breve!', active: true },
    { id: 4, title: 'Em Caso de Ausência (No-show)', color: 'gray', text: '“Oi! Tivemos algum imprevisto? Se quiser, podemos remarcar tocando aqui.”', active: true }
  ]);

  // Handlers
  const handleSend = () => {
    if (!msg.trim()) return;
    const userText = msg.trim();
    const newChat = [...chat, { role: 'user', text: userText }];
    setChat(newChat);
    setMsg('');
    setIsAiTyping(true);

    setTimeout(() => {
      let reply = '';
      const lower = userText.toLowerCase();
      if (lower.includes('preço') || lower.includes('valor') || lower.includes('quanto')) {
        reply = `Nossos valores variam conforme o pacote e sua necessidade específica. Posso te apresentar as opções em detalhes ou conectar você agora com nosso consultor comercial!`;
      } else if (lower.includes('humano') || lower.includes('falar com alguém') || lower.includes('atendente')) {
        reply = `Entendido! Estou transferindo seu atendimento para nossa equipe humana agora mesmo. Um momento, por favor! ⏳`;
      } else if (lower.includes('horário') || lower.includes('agendar') || lower.includes('marcar')) {
        reply = `Perfeito! Temos horários disponíveis esta semana. Você prefere período da manhã ou da tarde?`;
      } else {
        reply = `Com certeza! Na ${client.data.name}, trabalhamos exatamente para resolver isso com a máxima qualidade e agilidade. Gostaria de agendar uma demonstração ou receber uma proposta?`;
      }
      setChat([...newChat, { role: 'ai', text: reply }]);
      setIsAiTyping(false);
    }, 600);
  };

  const handleAddSource = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSourceTitle.trim()) return;
    setSources([
      ...sources,
      { id: Date.now(), title: newSourceTitle.trim(), size: '150 KB', updated: 'Hoje' }
    ]);
    toast.success('Documento indexado no cérebro da IA!');
    setNewSourceTitle('');
    setIsAddSourceModalOpen(false);
  };

  const handleAddRule = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRuleName.trim() || !newRuleQuestion.trim()) {
      toast.error('Preencha o nome e a pergunta da regra');
      return;
    }
    setRules([
      ...rules,
      { id: Date.now(), name: newRuleName.trim(), type: newRuleType, question: newRuleQuestion.trim() }
    ]);
    toast.success('Regra de qualificação adicionada!');
    setNewRuleName('');
    setNewRuleQuestion('');
    setIsAddRuleModalOpen(false);
  };

  return (
    <div className="h-full flex flex-col w-full max-w-7xl mx-auto pb-12 animate-in fade-in duration-300">
      
      {/* Header */}
      <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
            <span>Agente Comercial & Vendedor WhatsApp</span>
            <span className={`text-xs px-3 py-1 rounded-full font-bold border ${
              isConnected 
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                : 'bg-rose-50 text-rose-700 border-rose-200'
            }`}>
              {isConnected ? '● Conectado (Online)' : '○ Desconectado'}
            </span>
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Atendimento humanizado com IA generativa, qualificação de leads e transferência para a equipe.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsQrModalOpen(true)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2"
          >
            <QrCode size={16} />
            {isConnected ? 'Reconectar WhatsApp' : 'Ler QR Code'}
          </button>
        </div>
      </div>

      {/* Tabs Switcher */}
      <div className="flex bg-white p-1 rounded-xl w-fit mb-6 shadow-sm border border-gray-200 overflow-x-auto max-w-full hide-scrollbar">
        {[
          { id: 'geral', label: 'Visão Geral & Conexão', icon: MessageSquare },
          { id: 'cerebro', label: 'Cérebro & Simulador', icon: Bot },
          { id: 'filtro', label: 'Regras de Qualificação', icon: Funnel },
          { id: 'handoff', label: 'Transbordo (Humano)', icon: Users },
          { id: 'agenda', label: 'Agenda & Lembretes', icon: Calendar },
        ].map(item => (
          <button 
            key={item.id}
            onClick={() => setActiveTab(item.id as any)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === item.id ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <item.icon size={16} className={activeTab === item.id ? 'text-white' : 'text-gray-400'} />
            {item.label}
          </button>
        ))}
      </div>

      {/* Tab 1: Geral & Conexao */}
      {activeTab === 'geral' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                <Smartphone size={22} />
              </div>
              <div>
                <div className="text-xl font-bold text-gray-900 leading-none">{phoneNumber}</div>
                <p className="text-gray-900 text-sm font-semibold mt-1">Número Conectado</p>
                <p className="text-xs text-emerald-600 font-medium mt-0.5">Sessão Ativa & Estável</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                <MessageSquare size={22} />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 leading-none">128</div>
                <p className="text-gray-900 text-sm font-semibold mt-1">Atendimentos no Mês</p>
                <p className="text-xs text-gray-400 mt-0.5">Tempo de resposta: &lt; 3 seg</p>
              </div>
            </div>

            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
                <Zap size={22} />
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600 leading-none">88%</div>
                <p className="text-gray-900 text-sm font-semibold mt-1">Qualificação Automática</p>
                <p className="text-xs text-gray-400 mt-0.5">Leads com dados completos</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-1">
              <h3 className="text-lg font-bold text-gray-900">Status do Agente WhatsApp</h3>
              <p className="text-xs text-gray-500 max-w-xl">
                Quando ativado, a inteligência artificial responde imediatamente mensagens recebidas de novos leads, qualifica as respostas e notifica os vendedores responsáveis.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setIsConnected(!isConnected);
                  toast.success(isConnected ? 'Agente pausado.' : 'Agente ativado e online!');
                }}
                className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm flex items-center gap-2 ${
                  isConnected 
                    ? 'bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200' 
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                {isConnected ? 'Pausar Atendimento IA' : 'Ativar Atendimento IA'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Cérebro & Simulador */}
      {activeTab === 'cerebro' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in fade-in duration-300">
          
          {/* Configurações do Cérebro */}
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Base de Conhecimento (RAG)</h3>
                  <p className="text-xs text-gray-500">Documentos que a IA consulta para responder seus clientes.</p>
                </div>
                <button
                  onClick={() => setIsAddSourceModalOpen(true)}
                  className="bg-black hover:bg-gray-800 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                >
                  <Plus size={13} /> + Adicionar Fonte
                </button>
              </div>

              <div className="space-y-2">
                {sources.map(src => (
                  <div key={src.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs">
                    <div className="flex items-center gap-2.5">
                      <Database size={15} className="text-indigo-600" />
                      <div>
                        <p className="font-bold text-gray-900">{src.title}</p>
                        <p className="text-gray-400 text-[10px]">{src.size} • Atualizado {src.updated}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSources(sources.filter(s => s.id !== src.id));
                        toast.success('Documento removido da base.');
                      }}
                      className="text-gray-400 hover:text-red-500 p-1"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
              <h3 className="text-lg font-bold text-gray-900">Tom de Voz & Prompt Master</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPersonality('consultivo')}
                  className={`p-3 rounded-xl border text-left text-xs transition-all ${
                    personality === 'consultivo'
                      ? 'border-indigo-600 bg-indigo-50/40 ring-2 ring-indigo-600/20'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <p className="font-bold text-gray-900">Consultivo & Empático</p>
                  <p className="text-gray-500 mt-0.5">Explica, ouve e tira dúvidas antes da oferta.</p>
                </button>

                <button
                  type="button"
                  onClick={() => setPersonality('direto')}
                  className={`p-3 rounded-xl border text-left text-xs transition-all ${
                    personality === 'direto'
                      ? 'border-indigo-600 bg-indigo-50/40 ring-2 ring-indigo-600/20'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <p className="font-bold text-gray-900">Direto & Foco em Fechamento</p>
                  <p className="text-gray-500 mt-0.5">Objetivo, focado em agendamento rápido.</p>
                </button>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Instrução Principal do Robô
                </label>
                <textarea 
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  rows={4}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs font-mono text-gray-800 outline-none focus:bg-white focus:ring-2 focus:ring-black"
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => toast.success('Prompt e Personalidade salvos com sucesso!')}
                  className="bg-black hover:bg-gray-800 text-white text-xs font-bold px-4 py-2 rounded-xl transition-colors flex items-center gap-1.5 shadow-sm"
                >
                  <Save size={13} /> Salvar Instruções
                </button>
              </div>
            </div>
          </div>

          {/* Simulador Interativo */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col h-[600px] overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xs">
                  IA
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm">Simulador de Conversa</h4>
                  <p className="text-[10px] text-emerald-600 font-semibold">Online • Teste em tempo real</p>
                </div>
              </div>

              <button
                onClick={() => setChat([{ role: 'ai', text: `Olá! Sou a assistente virtual da ${client.data.name}. Como posso te ajudar hoje?` }])}
                className="text-gray-400 hover:text-gray-700 p-1.5 rounded-lg"
                title="Reiniciar conversa"
              >
                <RefreshCw size={14} />
              </button>
            </div>

            {/* Messages body */}
            <div className="flex-1 p-4 overflow-y-auto space-y-3 bg-[#E5DDD5]/30">
              {chat.map((c, i) => (
                <div key={i} className={`flex ${c.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[82%] p-3 rounded-2xl text-xs shadow-sm ${
                    c.role === 'user' 
                      ? 'bg-[#DCF8C6] text-gray-900 rounded-tr-none' 
                      : 'bg-white text-gray-900 rounded-tl-none border border-gray-200'
                  }`}>
                    <p className="leading-relaxed">{c.text}</p>
                    <span className="block text-[9px] text-gray-400 text-right mt-1">Agora</span>
                  </div>
                </div>
              ))}
              {isAiTyping && (
                <div className="flex justify-start">
                  <div className="bg-white p-3 rounded-2xl rounded-tl-none border border-gray-200 text-xs text-gray-500 shadow-sm flex items-center gap-1.5 animate-pulse">
                    <span>Lia está digitando</span>
                    <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></span>
                  </div>
                </div>
              )}
            </div>

            {/* Input area */}
            <div className="p-3 border-t border-gray-200 bg-white flex gap-2">
              <input 
                type="text"
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Escreva como se fosse um cliente..."
                className="flex-1 px-3.5 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs outline-none focus:bg-white focus:ring-2 focus:ring-black"
              />
              <button
                onClick={handleSend}
                className="bg-emerald-600 hover:bg-emerald-700 text-white p-2.5 rounded-xl transition-colors shadow-sm"
              >
                <Send size={15} />
              </button>
            </div>
          </div>

        </div>
      )}

      {/* Tab 3: Filtro de Qualificacao */}
      {activeTab === 'filtro' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Perguntas de Qualificação Obrigatórias</h3>
                <p className="text-xs text-gray-500">A IA coleta essas respostas antes de repassar a oportunidade ao CRM ou vendedor.</p>
              </div>

              <button
                onClick={() => setIsAddRuleModalOpen(true)}
                className="bg-black hover:bg-gray-800 text-white text-xs font-bold px-4 py-2 rounded-xl transition-colors flex items-center gap-1.5 shadow-sm"
              >
                <Plus size={14} /> + Nova Pergunta
              </button>
            </div>

            <div className="space-y-4">
              {rules.map((rule, idx) => (
                <div key={rule.id} className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <h4 className="font-bold text-gray-900 text-sm">{rule.name}</h4>
                      <span className="text-[10px] uppercase font-bold text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">
                        {rule.type}
                      </span>
                    </div>

                    <button
                      onClick={() => {
                        setRules(rules.filter(r => r.id !== rule.id));
                        toast.success('Pergunta removida.');
                      }}
                      className="text-gray-400 hover:text-red-500 p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                      Pergunta feita pela IA:
                    </label>
                    <input 
                      type="text"
                      value={rule.question}
                      onChange={(e) => {
                        const updated = [...rules];
                        updated[idx].question = e.target.value;
                        setRules(updated);
                      }}
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-6 border-t border-gray-100 flex justify-end mt-6">
              <button
                onClick={() => toast.success('Fluxo de qualificação atualizado com sucesso!')}
                className="bg-black hover:bg-gray-800 text-white font-bold text-xs px-5 py-2.5 rounded-xl transition-all shadow-sm"
              >
                Salvar Fluxo de Qualificação
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab 4: Transbordo */}
      {activeTab === 'handoff' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in duration-300">
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h3 className="text-lg font-bold text-gray-900">Gatilhos de Transferência (Handoff)</h3>
            <p className="text-xs text-gray-500">Quando a inteligência artificial deve acionar o humano responsável:</p>

            <div className="space-y-3">
              {handoffTriggers.map((trig, idx) => (
                <div key={trig.id} className="p-3.5 bg-gray-50 border border-gray-200 rounded-xl flex items-start gap-3">
                  <input 
                    type="checkbox"
                    checked={trig.active}
                    onChange={() => {
                      const updated = [...handoffTriggers];
                      updated[idx].active = !updated[idx].active;
                      setHandoffTriggers(updated);
                    }}
                    className="mt-1 accent-black"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900 text-xs">{trig.label}</h4>
                    <p className="text-gray-500 text-[11px] mt-0.5">{trig.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-gray-100">
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Canais de Notificação</h4>
              <div className="space-y-2 text-xs">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={notifyWhatsapp}
                    onChange={(e) => setNotifyWhatsapp(e.target.checked)}
                    className="accent-black" 
                  />
                  <span className="font-medium text-gray-800">Alerta no WhatsApp do Vendedor Atribuído</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={notifyCrm}
                    onChange={(e) => setNotifyCrm(e.target.checked)}
                    className="accent-black" 
                  />
                  <span className="font-medium text-gray-800">Criar Lead no Smart CRM Automaticamente</span>
                </label>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                onClick={() => toast.success('Configurações de transbordo salvas!')}
                className="bg-black hover:bg-gray-800 text-white font-bold text-xs px-4 py-2 rounded-xl transition-all shadow-sm"
              >
                Salvar Gatilhos
              </button>
            </div>
          </div>

          <div className="bg-gray-900 text-white p-6 rounded-2xl shadow-sm flex flex-col justify-between">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 bg-emerald-950 px-2.5 py-1 rounded border border-emerald-800">
                Preview da Notificação para o Vendedor
              </span>
              <h4 className="font-bold text-base mt-4">Resumo Automático do Lead</h4>
              
              <div className="mt-4 p-4 bg-white/10 rounded-xl text-xs font-mono space-y-2 border border-white/10 text-gray-200">
                <p>🔔 <strong>NOVO LEAD QUALIFICADO!</strong></p>
                <p>👤 <strong>Nome:</strong> Carlos Eduardo</p>
                <p>📱 <strong>WhatsApp:</strong> 11 98888-2222</p>
                <p>🎯 <strong>Interesse:</strong> {client.data.categories?.[0] || 'Serviço Principal'}</p>
                <p>💰 <strong>Expectativa:</strong> Imediato</p>
                <p className="pt-2 border-t border-white/10 text-emerald-300">
                  ✨ <em>Resumo IA: Lead quer proposta formal hoje. Atribuído a você pelo sistema.</em>
                </p>
              </div>
            </div>

            <p className="text-[11px] text-gray-400 mt-6">
              Essa mensagem chega diretamente no WhatsApp do atendente responsável assim que o lead termina de responder o formulário.
            </p>
          </div>
        </div>
      )}

      {/* Tab 5: Agenda & Lembretes */}
      {activeTab === 'agenda' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm space-y-4">
            <h3 className="text-lg font-bold text-gray-900">Régua de Notificações WhatsApp</h3>
            <p className="text-xs text-gray-500">Disparos de mensagens que a IA envia automaticamente para cada compromisso:</p>

            <div className="space-y-3">
              {reminders.map((rem, idx) => (
                <div key={rem.id} className="p-4 bg-gray-50 border border-gray-200 rounded-xl space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                      <h4 className="font-bold text-gray-900 text-xs">{rem.title}</h4>
                    </div>
                    <label className="flex items-center gap-2 text-xs text-gray-600 font-medium">
                      <input 
                        type="checkbox"
                        checked={rem.active}
                        onChange={() => {
                          const updated = [...reminders];
                          updated[idx].active = !updated[idx].active;
                          setReminders(updated);
                        }}
                        className="accent-black"
                      />
                      <span>Ativo</span>
                    </label>
                  </div>
                  <textarea 
                    value={rem.text}
                    onChange={(e) => {
                      const updated = [...reminders];
                      updated[idx].text = e.target.value;
                      setReminders(updated);
                    }}
                    rows={2}
                    className="w-full p-2.5 bg-white border border-gray-200 rounded-lg text-xs font-mono text-gray-700 outline-none focus:ring-2 focus:ring-black"
                  />
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => toast.success('Lembretes sincronizados com o Agente WhatsApp!')}
                className="bg-black hover:bg-gray-800 text-white font-bold text-xs px-5 py-2.5 rounded-xl transition-all shadow-sm"
              >
                Salvar Régua Anti No-Show
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Adicionar Documento */}
      {isAddSourceModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-4">
              <h3 className="font-bold text-gray-900 text-lg">Adicionar Documento à IA</h3>
              <button 
                onClick={() => setIsAddSourceModalOpen(false)}
                className="text-gray-400 hover:text-gray-900 p-1.5 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAddSource} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Título ou Nome do Arquivo
                </label>
                <input 
                  type="text"
                  required
                  placeholder="Ex: Tabela de Preços e Formas de Pagamento.pdf"
                  value={newSourceTitle}
                  onChange={(e) => setNewSourceTitle(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                />
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsAddSourceModalOpen(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-black hover:bg-gray-800 text-white px-5 py-2.5 text-sm font-bold rounded-xl shadow-sm"
                >
                  Indexar Documento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Nova Regra */}
      {isAddRuleModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-4">
              <h3 className="font-bold text-gray-900 text-lg">Nova Pergunta de Qualificação</h3>
              <button 
                onClick={() => setIsAddRuleModalOpen(false)}
                className="text-gray-400 hover:text-gray-900 p-1.5 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAddRule} className="space-y-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Identificador da Regra
                </label>
                <input 
                  type="text"
                  required
                  placeholder="Ex: Urgência do Cliente"
                  value={newRuleName}
                  onChange={(e) => setNewRuleName(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Tipo
                </label>
                <select
                  value={newRuleType}
                  onChange={(e) => setNewRuleType(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                >
                  <option value="qualitativo">Qualitativo (Texto Livre)</option>
                  <option value="quantitativo">Quantitativo (Valor / Prazo)</option>
                  <option value="multipla_escolha">Múltipla Escolha</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                  Pergunta Feita pela IA
                </label>
                <input 
                  type="text"
                  required
                  placeholder="Ex: Para quando você pretende iniciar?"
                  value={newRuleQuestion}
                  onChange={(e) => setNewRuleQuestion(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:bg-white focus:ring-2 focus:ring-black"
                />
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsAddRuleModalOpen(false)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="bg-black hover:bg-gray-800 text-white px-5 py-2.5 text-sm font-bold rounded-xl shadow-sm"
                >
                  Salvar Pergunta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: QR Code Simulator */}
      {isQrModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl border border-gray-200 text-center">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-gray-900 text-base">Conectar WhatsApp Web</h3>
              <button onClick={() => setIsQrModalOpen(false)} className="text-gray-400 hover:text-gray-900 p-1">
                <X size={18} />
              </button>
            </div>

            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200 inline-block my-2">
              <img 
                src="https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=NYROH_WHATSAPP_CONNECT_SIMULATOR" 
                alt="QR Code de Conexão"
                className="w-44 h-44 mx-auto rounded-lg"
              />
            </div>

            <p className="text-xs text-gray-500 mt-3 leading-relaxed">
              Abra o WhatsApp no seu celular, vá em <strong>Aparelhos Conectados</strong> e aponte para a câmera.
            </p>

            <button
              onClick={() => {
                setIsConnected(true);
                setIsQrModalOpen(false);
                toast.success('WhatsApp sincronizado com sucesso!');
              }}
              className="w-full mt-5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm py-2.5 rounded-xl transition-colors shadow-sm"
            >
              Simular Conexão Concluída
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
